/**
 * Event form — a single-page progressive form, each concern its own
 * elevated `schmancy-surface` card so the page reads step by step even
 * though it stays one scroll, no wizard. Section order fronts the
 * structurally required path — Basics → Schedule & venue → Ticket types —
 * before the optional Artwork step, with the administrative Status & limits
 * card last. Mounted via `show()` as an overlay, or full-bleed as a page —
 * the form's own root fills whatever box its mounter gives it and carries
 * no overlay/page branching; it closes by dispatching `close`.
 *
 * Ticket types: both modes open the same `<ticket-type-form>` overlay.
 * Editing an existing event opens it `mode="live"` (unset — its default) —
 * `ticketTypesState`/`ticketTypesAction$` own the real per-row CRUD, as
 * before. Create mode has no event id yet for a ticketTypes subcollection
 * write to target, so it opens the overlay `mode="draft"` instead: the
 * overlay composes a `TicketTypeDraft` locally (kind/name/price/VAT/capacity
 * plus a showAfterSoldOut chained against sibling drafts by draftId — no
 * sales window, no venue override, neither exists on a draft yet) and hands
 * it back via a `draft-save` event; this component just accumulates the
 * list into `draftTicketTypes`, riding it on the create intent's
 * `EventWrite.ticketTypes` — the write reconciler mints the subcollection
 * docs (and their salesWindow defaults) once the event id exists. Either
 * list's rows, and the open form itself, carry a Duplicate gesture: both
 * reopen the same overlay over that row as a copy — `duplicateOf` in, a
 * `close` carrying `{ duplicateOf, seed }` out, the copy's overlay opening
 * once the old one has settled — and nothing is written until the copy's
 * own Save.
 *
 * Artwork is real: `<schmancy-image-crop>` owns the single upload+crop path,
 * ratio and long-edge cap both bound to `Event.POSTER_RATIO`/`POSTER_HEIGHT`.
 * 2:3 matches the poster Storage path's own portrait ratio, and at 2:3 the
 * long edge IS the height — so the Blob resolves at exactly POSTER_WIDTH ×
 * POSTER_HEIGHT, with no second literal free to drift from the contract. On
 * an existing event that Blob is dispatched straight down the poster-only
 * channel (`setPoster`), so the chosen poster survives leaving the form
 * without pressing Save; on a new event, which has no doc to patch yet, it
 * rides the create intent's `artwork: EventArtwork` field instead. Either way
 * this form stays zero-I/O; it never touches Storage directly. Its column is
 * capped narrow (`max-w-xs`) so the dropzone's own `aspect-ratio` sizing
 * (width-driven, tall at 2:3) reads as a bounded poster thumbnail rather than
 * growing with the page's full reading-column width. "Generate with AI" is
 * parked (not rendered) — `artworkGenerate$`/`generateArtwork()`/the `ai*`
 * component state stay wired and dormant, ready for a future tile to
 * re-enable with no re-plumb.
 * events.sync.ts (web-side) is the sole subscriber that calls the callable
 * and uploads the chosen blob to Storage.
 *
 * Logo — the event's own wordmark — lives in the "Ticket look" card beside
 * the template picker rather than beside the poster: it drives a variant's
 * rendering (event.entity.ts's `logo` field), not the storefront browse
 * grid, and no fixed ratio is forced on it (unlike the poster's 2:3 crop). A
 * plain file input, not `<schmancy-image-crop>` — organization-settings.view.ts's
 * workspace-brand-logo pattern, not the poster's. Unlike the poster's crop, a
 * picked logo is queued like every other field on this form
 * (`logo: EventArtwork`) and lands only on this form's own Save; there is no
 * logo-only instant-commit channel mirroring `setPoster`.
 *
 * `availability` never appears here — event.entity.ts's member-update rule
 * denies a write that touches it; this form's patch never carries the key.
 *
 * Languages, two surfaces. The card sits after Artwork, last before Status &
 * limits — every content section is filled in by then, so the bulk pass has
 * something to translate and reads as the final content-review step before
 * publishing, not a stray non sequitur between Ticket types and Returns. It
 * covers the whole event at once (the paid pass over description and every ticket
 * type together), and its own hint names the alternative so the two surfaces
 * don't read as a duplicate: `<localized-text>` rides in the description field's
 * own `actions` slot and covers that one field,
 * opening its versions for a hand correction and patching the map alone
 * through `copyFieldAction$` — never through this form's own Save, which
 * writes fields the operator may still be typing. Both read the STORED description,
 * so both are locked while an unsaved edit to it stands: a version written
 * from a sentence this form is about to replace is a version its own Save then
 * retires. Why that lock stands, why a pass that failed left nothing behind,
 * and why a field with nothing stored can be translated by nobody are one
 * sentence under the field, and `<localize-notice>` owns which one — the
 * `actions` slot takes controls, and a paragraph in it would overflow the box
 * it sits in.
 *
 * Returns: an edit-mode-only card between "Ticket types" and "Ticket look",
 * behind one switch — an absent `Event.refund` means returns are closed, so
 * OFF is the default and the card writes null while off. Edit-mode-only
 * because `refundableTypeIds` names ticket types by their real subcollection
 * id, which a create-mode draft (draftId only) does not have yet; on a brand
 * new event every type would also be unticked, turning the whitelist's own
 * misconfiguration alarm into noise on every create.
 *
 * That whitelist is closed — an unticked type is not refundable and a tier
 * created tomorrow stays unticked — so the card states the consequence three
 * ways instead of leaving it inferred from empty boxes: the negative named as
 * its own sentence, refunds-on-with-nothing-ticked called out as a live
 * misconfiguration, and a standing line about future types (nothing can
 * detect that gap later, so nothing conditional can warn about it). One hint
 * line per control, never a paragraph.
 *
 * `refund.paidBefore` is a full instant, not a day — refunds open because
 * something was announced at an hour — so it is a `datetime-local`
 * `<schmancy-input>`, capped `max-w-xs` because at full width the control's
 * own `justify-content: space-between` scatters its segments and it stops
 * reading as one instant. Empty is the unset field: turning the switch on
 * asks for a value rather than fabricating "now", and turning it off clears
 * it.
 */

import { formatCurrency, formatDateRange } from "@mhmo91/rx";
import {
	overflowWithin,
	reorder,
	type SchmancyReorderEvent,
} from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { $notify } from "@mhmo91/schmancy/notification";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import {
	Dependents,
	Event,
	Location,
	type TicketTemplateVariant,
	TicketType,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	EMPTY,
	filter,
	firstValueFrom,
	map,
	merge,
	take,
	takeUntil,
	tap,
} from "rxjs";
import "../catalog/delete-refusal.view.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import { locationsState } from "../storefront/locations.state.ts";
import {
	consoleEventId,
	consoleFiguresByType,
	consoleOrders,
	consoleTickets,
	consoleTicketsListening,
} from "./console/console.state.ts";
import "../locations/select-location.view.ts";
import "../ticket-templates/ticket-template-picker.view.ts";
import "./event-tile.view.ts";
import { copyFieldSettled$ } from "./localize/copy-field.state.ts";
import "./localize/localize-copy.view.ts";
import "./localize/localize-notice.view.ts";
import type {
	LocalizedTextSaveEvent,
	LocalizedTextTranslateEvent,
} from "./localize/localized-text.view.ts";
import "./localize/localized-text.view.ts";
import {
	type ArtworkCandidate,
	artworkGenerate$,
	artworkGenerateResult$,
	type EventArtwork,
	type EventRefundWrite,
	type EventTileRow,
	type EventWrite,
	eventsAction$,
	eventsResult$,
	eventsState,
	resolveVenueLabel,
	resolveVenueTimezone,
	type TicketTypeDraft,
} from "./events.state.ts";
import "./ticket-types/day-clone-sheet.view.ts";
import "./ticket-types/ticket-type-form.view.ts";
import type {
	TicketTypeFormResult,
	TicketTypeFormSeed,
} from "./ticket-types/ticket-type-form.view.ts";
import {
	type TicketTypeWrite,
	ticketTypesAction$,
	ticketTypesPageView,
	ticketTypesResult$,
	ticketTypesState,
} from "./ticket-types/ticket-types.state.ts";
import "./programme-calendar.view.ts";

const STATUSES: readonly Event.Event["status"][] = [
	"draft",
	"published",
	"archived",
];

// Mirrors storage.rules' own `logo.(jpg|jpeg|png|webp)` mask and its 2MB cap
// (half the poster's 5MB — a wordmark carries far fewer pixels).
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

@schmancyElement("event-form")
export class EventForm extends LocalizeMixin(SchmancyElement) {
	/** Empty means create; set means edit that event's own id. Overrides the inherited HTMLElement.id. */
	@property({ type: String }) override id = "";

	@stateDecorator() private name = "";
	@stateDecorator() private description = "";
	@stateDecorator() private start = "";
	@stateDecorator() private end = "";
	@stateDecorator() private locationId = "";
	/** The event's own default rendering variant — null is the
	 *  inherit-the-package's-built-in-default state. */
	@stateDecorator() private templateVariant: TicketTemplateVariant | null =
		null;
	@stateDecorator() private status: Event.Event["status"] = "draft";
	@stateDecorator() private maxTicketsPerOrder = "10";
	@stateDecorator() private saving = false;
	@stateDecorator() private activeAction: "save" | "delete" | null = null;
	@stateDecorator() private failedAction: "save" | "delete" | null = null;
	/**
	 * The description as it stands on disk, snapshotted at mount. An edit away from
	 * it is what retires `descriptionByLocale`: the stored map was drafted from
	 * this exact string, so the moment the operator writes a different one the
	 * map describes copy nobody serves any more. Null for a create — there is
	 * no stored map to retire, and writing the explicit null says the same
	 * thing the absent key would.
	 */
	private storedDescription: string | null = null;

	// Artwork — see file header. "Generate with AI" is parked: the "ai" choice
	// still renders below (so aiPrompt/aiGenerating/aiCandidates/aiSelectedId/
	// aiError and generateArtwork()/selectAiCandidate() stay genuinely wired,
	// not decorative dead code) but is unreachable — no tile sets
	// artworkChoice to "ai" anymore. Re-enabling it later is one tile, not a
	// re-plumb.
	@stateDecorator() private artworkChoice: "unset" | "ai" | "done" = "unset";
	@stateDecorator() private aiPrompt = "";
	@stateDecorator() private aiGenerating = false;
	@stateDecorator() private aiCandidates: readonly ArtworkCandidate[] = [];
	@stateDecorator() private aiSelectedId: string | null = null;
	@stateDecorator() private aiError: string | null = null;
	/** The newly chosen artwork's ready-to-upload bytes — null until a crop or an AI candidate is picked. */
	@stateDecorator() private artworkBlob: Blob | null = null;
	/** Explicit removal of a previously-existing poster, distinct from "nothing chosen yet". */
	@stateDecorator() private artworkRemoved = false;
	/** Preview URL shown in the "done" frame — an existing posterUrl, or an object URL over a new crop/AI blob. */
	@stateDecorator() private posterPreview: string | null = null;

	// Logo — the event's own wordmark image, distinct from the poster: no
	// forced crop ratio (a wordmark isn't cropped to a fixed ratio, unlike the
	// poster), so this follows organization-settings.view.ts's own plain
	// file-input pattern for the workspace brand logo rather than reusing
	// <schmancy-image-crop>. Unlike the poster's crop, which commits instantly
	// on an existing event (setPoster), a picked logo is queued like every
	// other field on this form and lands only on Save.
	private readonly logoInputRef = createRef<HTMLInputElement>();
	/** The newly chosen logo's ready-to-upload bytes — null until picked. */
	@stateDecorator() private logoBlob: Blob | null = null;
	/** Explicit removal of an existing logo, distinct from "nothing chosen yet". */
	@stateDecorator() private logoRemoved = false;
	/** An existing logoUrl, or an object URL over a newly picked file — null once removed. */
	@stateDecorator() private logoPreviewUrl: string | null = null;

	// Create-mode ticket-type list — see file header. Both modes now open the
	// same <ticket-type-form> overlay; this is just the accumulated list.
	@stateDecorator() private draftTicketTypes: readonly TicketTypeDraft[] = [];

	// The operator's chosen id order once a drop has fired, rendered ahead of
	// the write's own Firestore round-trip. The drafts list needs no
	// counterpart: its drop reassigns draftTicketTypes directly, and the array
	// itself IS the order.
	@stateDecorator() private optimisticTicketTypeOrder:
		| readonly string[]
		| null = null;

	// Returns — see the "Returns" section in the file header. Off is the
	// default because an absent Event.refund IS "returns closed".
	@stateDecorator() private refundOn = false;
	/** Naive wall-clock, resolved against the venue's zone at save time, exactly
	 *  like `start`/`end` above. */
	@stateDecorator() private refundStart = "";
	@stateDecorator() private refundEnd = "";
	@stateDecorator() private paidBeforeOn = false;
	/** Empty is the unset field, never an implied "now" — turning the switch on
	 *  asks for a value rather than fabricating one. */
	@stateDecorator() private paidBefore = "";
	@stateDecorator() private refundMessageDe = "";
	@stateDecorator() private refundMessageEn = "";
	/** The closed whitelist: a type absent from it is not refundable. */
	@stateDecorator() private refundableTypeIds: readonly string[] = [];

	override connectedCallback(): void {
		super.connectedCallback();
		const canonical = this.id
			? eventsState.value.find((e) => e.id === this.id)
			: undefined;
		if (canonical) {
			this.name = canonical.name;
			this.description = canonical.description ?? "";
			// Trimmed, because both readers compare against the value about to be
			// written, which is itself trimmed — a stored description carrying a
			// trailing newline (pasted copy, a provisioning script, a seed) would
			// otherwise read as edited on an untouched mount, locking the
			// Languages action and retiring the map on the first Save.
			this.storedDescription = canonical.description?.trim() || null;
			const tz = resolveVenueTimezone(
				canonical.locationId,
				locationsState.value,
			);
			this.start = Location.instantToWallClock(
				canonical.schedule.start.toDate(),
				tz,
			);
			this.end = Location.instantToWallClock(
				canonical.schedule.end.toDate(),
				tz,
			);
			this.locationId = canonical.locationId;
			this.templateVariant = canonical.templateVariant ?? null;
			this.status = canonical.status;
			this.maxTicketsPerOrder = String(canonical.maxTicketsPerOrder);
			if (canonical.posterUrl) {
				this.posterPreview = canonical.posterUrl;
				this.artworkChoice = "done";
			}
			if (canonical.logoUrl) this.logoPreviewUrl = canonical.logoUrl;
			if (canonical.refund) {
				this.refundOn = true;
				this.refundStart = Location.instantToWallClock(
					canonical.refund.window.start.toDate(),
					tz,
				);
				this.refundEnd = Location.instantToWallClock(
					canonical.refund.window.end.toDate(),
					tz,
				);
				this.paidBeforeOn = !!canonical.refund.paidBefore;
				this.paidBefore = canonical.refund.paidBefore
					? Location.instantToWallClock(
							canonical.refund.paidBefore.toDate(),
							tz,
						)
					: "";
				this.refundMessageDe = canonical.refund.message?.de ?? "";
				this.refundMessageEn = canonical.refund.message?.en ?? "";
				this.refundableTypeIds = canonical.refund.refundableTypeIds;
			}
		}
		merge(
			// Once the reorder batch this optimistic render was standing in for
			// lands (or fails), ticketTypesState carries the true order — drop the
			// stand-in so the render reads straight off it again.
			ticketTypesResult$.pipe(
				filter((r) => r.kind === "reorder"),
				tap(() => {
					this.optimisticTicketTypeOrder = null;
				}),
			),
			// A new event at a workspace with exactly one venue can only be held
			// there, so the field starts on it. The catalog may land after mount,
			// and an operator's own pick is never overwritten.
			canonical
				? EMPTY
				: locationsState.$.pipe(
						filter((venues) => venues.length === 1 && this.locationId === ""),
						map(([venue]) => venue?.id ?? ""),
						take(1),
						tap((venueId) => {
							this.locationId = venueId;
						}),
					),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.posterPreview?.startsWith("blob:"))
			URL.revokeObjectURL(this.posterPreview);
		if (this.logoPreviewUrl?.startsWith("blob:"))
			URL.revokeObjectURL(this.logoPreviewUrl);
		for (const c of this.aiCandidates) URL.revokeObjectURL(c.imageUrl);
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async generateArtwork(): Promise<void> {
		if (this.aiGenerating) return;
		const prompt = this.aiPrompt.trim();
		if (!prompt) {
			$notify.error(this.t("admin.events.form.generateArtworkEmptyPrompt"));
			return;
		}
		this.aiGenerating = true;
		this.aiError = null;
		const requestId = crypto.randomUUID();
		const resultPromise = firstValueFrom(
			artworkGenerateResult$.pipe(
				filter((r) => r.requestId === requestId),
				takeUntil(this.disconnecting),
			),
		);
		artworkGenerate$.next({ requestId, prompt, styleHint: null });
		const result = await resultPromise;
		this.aiGenerating = false;
		if (!result.ok) {
			this.aiError =
				result.reason === "rate-limited"
					? this.t("admin.events.form.generateArtworkRateLimited")
					: result.reason === "unavailable"
						? this.t("admin.events.form.generateArtworkUnavailable")
						: this.t("admin.events.form.generateArtworkError");
			return;
		}
		for (const c of this.aiCandidates) URL.revokeObjectURL(c.imageUrl);
		this.aiCandidates = result.candidates;
		this.aiSelectedId = null;
	}

	/**
	 * A confirmed crop becomes this event's artwork at once. On an existing
	 * event it also lands on the doc immediately, through the poster-only
	 * channel, so leaving the form without pressing Save keeps the poster;
	 * dropping `artworkBlob` on success is what stops that Save from
	 * re-uploading the same bytes (validate() then reads "keep"), and keeping
	 * it on failure is what leaves the poster riding the form's own Save. A
	 * new event has no doc to patch — there the blob rides the create intent.
	 */
	private readonly applyArtwork = async (
		e: CustomEvent<{
			blob: Blob;
			file: File;
			width: number;
			height: number;
			ratio: string;
		}>,
	): Promise<void> => {
		const blob = e.detail.blob;
		this.artworkBlob = blob;
		this.artworkRemoved = false;
		this.posterPreview = URL.createObjectURL(blob);
		this.artworkChoice = "done";
		if (!this.id) return;
		const requestId = crypto.randomUUID();
		const resultPromise = firstValueFrom(
			eventsResult$.pipe(
				filter((r) => r.requestId === requestId),
				takeUntil(this.disconnecting),
			),
			// Leaving before the upload answers is the ordinary path here — the
			// poster landing without a Save is the point — and takeUntil
			// completing an unfulfilled firstValueFrom rejects. events.sync.ts's
			// reconciler is module-scope, so the write lands either way; this
			// component just has no one left to tell.
			{ defaultValue: null },
		);
		eventsAction$.next({ kind: "setPoster", requestId, id: this.id, blob });
		const result = await resultPromise;
		if (!result) return;
		if (!result.ok) {
			$notify.error(this.t("admin.events.form.posterSaveFailed"));
			return;
		}
		// Only if these are still the bytes on offer — a second crop while this
		// write was in flight owns the field now, and its own write clears it.
		if (this.artworkBlob === blob) this.artworkBlob = null;
	};

	private selectAiCandidate(id: string): void {
		const candidate = this.aiCandidates.find((c) => c.id === id);
		if (!candidate) return;
		this.artworkBlob = candidate.blob;
		this.artworkRemoved = false;
		this.posterPreview = candidate.imageUrl;
		this.aiSelectedId = id;
		this.artworkChoice = "done";
	}

	private changeArtwork(): void {
		this.artworkChoice = "unset";
		this.posterPreview = null;
	}

	private removeArtwork(): void {
		this.artworkBlob = null;
		this.artworkRemoved = true;
		this.posterPreview = null;
		this.artworkChoice = "unset";
	}

	private pickLogo(): void {
		this.logoInputRef.value?.click();
	}

	private onLogoFileChange(e: Event): void {
		const input = e.target as HTMLInputElement;
		const file = input.files?.[0] ?? null;
		input.value = ""; // allow re-picking the same file later
		if (!file) return;
		if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
			$notify.error(this.t("admin.events.form.logoUnsupportedTypeError"));
			return;
		}
		// >= : storage.rules gates on request.resource.size < 2 * 1024 * 1024
		// (strictly less than) — mirror the exact boundary so a file at exactly
		// the cap never passes here only to fail the upload with a raw error.
		if (file.size >= LOGO_MAX_BYTES) {
			$notify.error(this.t("admin.events.form.logoTooLargeError"));
			return;
		}
		if (this.logoPreviewUrl?.startsWith("blob:"))
			URL.revokeObjectURL(this.logoPreviewUrl);
		this.logoBlob = file;
		this.logoRemoved = false;
		this.logoPreviewUrl = URL.createObjectURL(file);
	}

	private removeLogo(): void {
		if (this.logoPreviewUrl?.startsWith("blob:"))
			URL.revokeObjectURL(this.logoPreviewUrl);
		this.logoBlob = null;
		this.logoRemoved = true;
		this.logoPreviewUrl = null;
	}

	private openTicketDraftForm(
		target: {
			draftId?: string;
			duplicateOf?: string;
			seed?: TicketTypeFormSeed;
		} = {},
	): void {
		show<TicketTypeFormResult>(
			() =>
				html`<ticket-type-form
					mode="draft"
					.id=${target.draftId ?? ""}
					.duplicateOf=${target.duplicateOf ?? ""}
					.duplicateSeed=${target.seed ?? null}
					.drafts=${this.draftTicketTypes}
					.draftSchedule=${
						this.start && this.end ? { start: this.start, end: this.end } : null
					}
					@draft-save=${(e: CustomEvent<TicketTypeDraft>) => {
						const draft = e.detail;
						this.draftTicketTypes = this.draftTicketTypes.some(
							(d) => d.draftId === draft.draftId,
						)
							? this.draftTicketTypes.map((d) =>
									d.draftId === draft.draftId ? draft : d,
								)
							: [...this.draftTicketTypes, draft];
					}}
				></ticket-type-form>`,
			{
				ariaLabel: this.t(
					target.draftId
						? "admin.events.form.overlayEditTicketType"
						: target.duplicateOf
							? "admin.events.form.overlayDuplicateTicketType"
							: "admin.events.form.overlayNewTicketType",
				),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((result) => {
				if (result)
					this.openTicketDraftForm({
						duplicateOf: result.duplicateOf,
						seed: result.seed,
					});
			});
	}

	private validate(): { body: EventWrite } | { error: string } {
		const name = this.name.trim();
		if (!name)
			return { error: this.t("admin.events.form.validation.nameRequired") };
		if (!this.locationId)
			return {
				error: this.t("admin.events.form.validation.venueRequired"),
			};
		if (!this.start || !this.end)
			return { error: this.t("admin.events.form.validation.datesRequired") };
		// Read fresh at save time — if the admin picked a different venue after
		// typing the times, the naive strings resolve under THAT venue's zone.
		const tz = resolveVenueTimezone(this.locationId, locationsState.value);
		const start = Location.wallClockToInstant(this.start, tz);
		const end = Location.wallClockToInstant(this.end, tz);
		if ("error" in start || "error" in end) {
			return {
				error: this.t("admin.events.form.validation.invalidTime", {
					time: "error" in start ? this.start : this.end,
					tz,
				}),
			};
		}
		if (end.getTime() <= start.getTime())
			return {
				error: this.t("admin.events.form.validation.endAfterStart"),
			};
		const maxTicketsPerOrder = Number(this.maxTicketsPerOrder);
		if (!Number.isInteger(maxTicketsPerOrder) || maxTicketsPerOrder < 1) {
			return {
				error: this.t("admin.events.form.validation.maxTicketsMin"),
			};
		}
		const artwork: EventArtwork = this.artworkBlob
			? { kind: "set", blob: this.artworkBlob }
			: this.artworkRemoved
				? { kind: "remove" }
				: { kind: "keep" };
		const logo: EventArtwork = this.logoBlob
			? { kind: "set", blob: this.logoBlob }
			: this.logoRemoved
				? { kind: "remove" }
				: { kind: "keep" };
		// Returns off writes null, which IS "returns closed" — the resolver reads
		// an absent map as not eligible, so there is no separate closed flag.
		let refund: EventRefundWrite | null = null;
		if (this.refundOn) {
			if (!this.refundStart || !this.refundEnd)
				return {
					error: this.t("admin.events.form.validation.refundWindowRequired"),
				};
			const refundStart = Location.wallClockToInstant(this.refundStart, tz);
			const refundEnd = Location.wallClockToInstant(this.refundEnd, tz);
			if ("error" in refundStart || "error" in refundEnd)
				return {
					error: this.t("admin.events.form.validation.invalidTime", {
						time: "error" in refundStart ? this.refundStart : this.refundEnd,
						tz,
					}),
				};
			if (refundEnd.getTime() <= refundStart.getTime())
				return {
					error: this.t("admin.events.form.validation.refundEndAfterStart"),
				};
			if (this.paidBeforeOn && !this.paidBefore)
				return {
					error: this.t(
						"admin.events.form.validation.refundPaidBeforeRequired",
					),
				};
			const paidBefore = this.paidBeforeOn
				? Location.wallClockToInstant(this.paidBefore, tz)
				: null;
			if (paidBefore && "error" in paidBefore)
				return {
					error: this.t("admin.events.form.validation.invalidTime", {
						time: this.paidBefore,
						tz,
					}),
				};
			// EventRefundConfig.message's own values are `.min(1)`, so an empty
			// box is the absent language, and both empty is the absent map — each
			// falls through to the return page's default copy.
			const messageDe = this.refundMessageDe.trim();
			const messageEn = this.refundMessageEn.trim();
			refund = {
				window: { start: refundStart, end: refundEnd },
				paidBefore,
				message:
					messageDe || messageEn
						? { de: messageDe || null, en: messageEn || null }
						: null,
				refundableTypeIds: [...this.refundableTypeIds],
			};
		}
		// The value actually going to disk, compared against the value the stored
		// map was drafted from — never the raw field, so a whitespace-only edit
		// that trims back to the same string leaves the map alone.
		const description = this.description.trim() || null;
		const descriptionEdited = description !== this.storedDescription;
		return {
			body: {
				name,
				description,
				// undefined leaves the stored map exactly as it is; null retires it.
				descriptionByLocale: descriptionEdited ? null : undefined,
				schedule: { start, end },
				locationId: this.locationId,
				templateVariant: this.templateVariant,
				status: this.status,
				maxTicketsPerOrder,
				artwork,
				logo,
				// Edit mode manages ticket types live (ticketTypesAction$) — this
				// channel only ever carries drafts on "create".
				ticketTypes: this.id ? [] : this.draftTicketTypes,
				// Create has no real ticket-type ids for the whitelist to name, so
				// the card renders in edit mode only and a create body carries null.
				refund: this.id ? refund : null,
			},
		};
	}

	/**
	 * One hand-corrected description map, patched onto the event document alone —
	 * never through this form's own Save, which writes every other field from
	 * whatever the operator has typed so far, accepted or not. A field rather
	 * than an inline arrow, so `@save` binds one stable reference across
	 * renders.
	 */
	private readonly storeDescriptionCopy = async (
		event: LocalizedTextSaveEvent,
	): Promise<void> => {
		const ok = await firstValueFrom(
			copyFieldSettled$({
				gesture: "correct",
				target: "event-description",
				eventId: this.id,
				byLocale: event.detail.byLocale,
			}).pipe(takeUntil(this.disconnecting)),
		).catch(() => false);
		event.detail.settle(ok);
	};

	/** The operator asked, from inside the versions overlay, for the description to
	 *  be written again. One field write and nothing beside it — asking twice,
	 *  or asking during a pass, costs nothing on the other side. */
	private readonly requestDescriptionPass = async (
		event: LocalizedTextTranslateEvent,
	): Promise<void> => {
		const ok = await firstValueFrom(
			copyFieldSettled$({
				gesture: "translate",
				target: "event-description",
				eventId: this.id,
			}).pipe(takeUntil(this.disconnecting)),
		).catch(() => false);
		event.detail.settle(ok);
	};

	private async save(): Promise<void> {
		if (this.saving) return;
		const validated = this.validate();
		if ("error" in validated) {
			$notify.error(validated.error);
			return;
		}
		this.saving = true;
		this.activeAction = "save";
		try {
			const ticketTypeCount = this.id
				? ticketTypesPageView.get().rows.length
				: validated.body.ticketTypes.length;
			if (validated.body.status === "published" && ticketTypeCount === 0) {
				const proceed = await confirm({
					title: this.t("admin.events.form.publishNoTicketTypesTitle"),
					message: this.t("admin.events.form.publishNoTicketTypesMessage"),
					confirmText: this.t("admin.events.form.publishAnyway"),
					cancelText: this.t("admin.events.form.addTicketTypeFirstCancelText"),
				});
				if (!proceed) return;
			}
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				eventsResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			eventsAction$.next(
				this.id
					? { kind: "update", requestId, id: this.id, body: validated.body }
					: { kind: "create", requestId, body: validated.body },
			);
			const result = await resultPromise;
			if (!result.ok) {
				this.failedAction = "save";
				$notify.error(result.error || this.t("admin.events.form.saveFailed"));
				return;
			}
			this.close();
		} catch {
			this.failedAction = "save";
			$notify.error(this.t("admin.events.form.saveFailed"));
		} finally {
			this.saving = false;
		}
	}

	/**
	 * Named to avoid colliding with the inherited Element.prototype.remove().
	 *
	 * The deleteEvent door refuses outright while any order, ticket or
	 * still-running checkout resolves through the event, and that refusal is
	 * a normal answer carrying counts — so an action the system is going to
	 * refuse asks no "are you sure?" first. Mounted inside the console
	 * (<event-console-edit>), this form can already SEE two of those three
	 * rows in the console's own live subcollections, so Delete goes straight
	 * to the refusal. Opened standalone from the event list, where neither
	 * subcollection is loaded, the confirm stays and the door's own census is
	 * the backstop.
	 *
	 * Only a POSITIVE local count short-circuits: `consoleOrders` is equally
	 * empty for a session `tickets.reports.view` never admitted, and
	 * `consoleTickets` for one without `tickets.checkin.scan`
	 * (consoleTicketsListening is that gate made readable), so a blank census
	 * means "cannot see", never "nothing in the way".
	 */
	private async deleteEvent(): Promise<void> {
		if (!this.id || this.saving) return;
		const id = this.id;
		const onConsole = consoleEventId.value === id;
		const seenOrders = onConsole ? consoleOrders.value.length : 0;
		const seenTickets =
			onConsole && consoleTicketsListening.get()
				? consoleTickets.value.length
				: 0;
		if (seenOrders > 0 || seenTickets > 0) {
			const seen: Dependents.DependentsInput = {
				orders: seenOrders,
				tickets: seenTickets,
			};
			this.openDeleteRefusal(Dependents.DependentsSchema.parse(seen));
			return;
		}
		this.saving = true;
		this.activeAction = "delete";
		try {
			const ok = await confirm({
				title: this.t("admin.events.form.deleteConfirmTitle"),
				message: this.t("admin.common.confirmCannotUndo"),
				confirmText: this.t("admin.common.delete"),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!ok) return;
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				eventsResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			eventsAction$.next({ kind: "delete", requestId, id });
			const result = await resultPromise;
			if (result.dependents) {
				this.openDeleteRefusal(result.dependents);
				return;
			}
			if (!result.ok) {
				this.failedAction = "delete";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "delete";
		} finally {
			this.saving = false;
		}
	}

	private openDeleteRefusal(dependents: Dependents.Dependents): void {
		show(
			() =>
				html`<delete-refusal entity="event" .dependents=${dependents}></delete-refusal>`,
			{
				sticky: true,
				ariaLabel: this.t("admin.events.form.deleteRefusalTitle"),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private openTicketTypeForm(
		target: {
			id?: string;
			duplicateOf?: string;
			seed?: TicketTypeFormSeed;
		} = {},
	): void {
		if (!this.id) return;
		// Reopened on the emission, not on a gesture mid-close: the emission
		// lands after the closing overlay has released its focus trap, so the
		// copy's overlay inerts the page and captures the focus to return to.
		show<TicketTypeFormResult>(
			() =>
				html`<ticket-type-form
					.eventId=${this.id}
					.id=${target.id ?? ""}
					.duplicateOf=${target.duplicateOf ?? ""}
					.duplicateSeed=${target.seed ?? null}
				></ticket-type-form>`,
			{
				ariaLabel: this.t(
					target.id
						? "admin.events.form.overlayEditTicketType"
						: target.duplicateOf
							? "admin.events.form.overlayDuplicateTicketType"
							: "admin.events.form.overlayNewTicketType",
				),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((result) => {
				if (result)
					this.openTicketTypeForm({
						duplicateOf: result.duplicateOf,
						seed: result.seed,
					});
			});
	}

	/** "Tage duplizieren" — opened from the day head the operator pressed in
	 *  the band below, which is what names `sourceDay`; the per-row duplicate
	 *  icon stays beside it for within-day copy-and-tweak. `scheduleDays` is
	 *  the event's own schedule-day range, resolved the same way
	 *  `ticket-type-form.view.ts`'s own entry-day chips are
	 *  (`Location.daysBetween` over the event's own first/last wall-clock
	 *  day) — the day-clone sheet's target-day candidate set. */
	private openDayCloneSheet(day: string): void {
		if (!this.id) return;
		const tz = resolveVenueTimezone(this.locationId, locationsState.value);
		const event = eventsState.value.find((e) => e.id === this.id);
		const scheduleDays = event
			? Location.daysBetween(
					Location.instantToWallClock(event.schedule.start.toDate(), tz).slice(
						0,
						10,
					),
					Location.instantToWallClock(event.schedule.end.toDate(), tz).slice(
						0,
						10,
					),
				)
			: [];
		show(
			() =>
				html`<day-clone-sheet
					.eventId=${this.id}
					.sourceDay=${day}
					.tz=${tz}
					.scheduleDays=${scheduleDays}
				></day-clone-sheet>`,
			{ ariaLabel: this.t("admin.ticketTypes.dayClone.title") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	// Built fresh for both the desktop inline pane and the narrow-viewport
	// overlay preview so the two never drift from the live form state.
	private previewRow(): EventTileRow {
		const tz = resolveVenueTimezone(this.locationId, locationsState.value);
		const start = this.start
			? Location.wallClockToInstant(this.start, tz)
			: null;
		const end = this.end ? Location.wallClockToInstant(this.end, tz) : null;
		return {
			id: this.id || "preview",
			name: this.name.trim() || "Untitled event",
			status: this.status,
			poster: this.posterPreview,
			startLabel:
				start && !("error" in start)
					? formatDateRange(start, end && !("error" in end) ? end : null, {
							month: "short",
							timeZone: tz,
						})
					: "Date TBD",
			venue: resolveVenueLabel(this.locationId, locationsState.value),
		};
	}

	// Narrow viewports drop the inline pane (see the render()'s @5xl split) —
	// this is the on-demand stand-in, the same <event-tile> the pane mounts.
	private openPreview(): void {
		show(
			() => html`
				<div class="mx-auto w-full max-w-xs p-4 min-w-64 ">
					<event-tile .row=${this.previewRow()} .interactive=${false}></event-tile>
				</div>
			`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render(): TemplateResult {
		const editing = !!this.id;
		const { rows: ticketTypeRows, status: ticketTypesStatus } =
			ticketTypesPageView.get();
		// A drop already committed to `ticketTypesAction$` is rendered ahead of
		// its own Firestore round-trip — the write reconciles ticketTypesState
		// (and clears this field) once the batch lands.
		const displayedTicketTypeRows = this.optimisticTicketTypeOrder
			? this.optimisticTicketTypeOrder
					.map((id) => ticketTypeRows.find((r) => r.id === id))
					.filter((r): r is (typeof ticketTypeRows)[number] => r != null)
			: ticketTypeRows;
		// The closed whitelist's own negative, enumerated rather than inferred
		// from which chips happen to be unticked.
		const refundExcluded = ticketTypeRows.filter(
			(r) => !this.refundableTypeIds.includes(r.id),
		);
		// The Languages pass reads the description from the STORED event, so an
		// uncommitted edit to it would be localized in its old form and then
		// retired by this same form's own Save — one paid call spent on copy
		// nobody will ever read.
		const descriptionUnsaved =
			(this.description.trim() || null) !== this.storedDescription;
		// The event as it stands ON DISK — the description the stored map was drafted
		// from, and the map itself, both read live so a correction that lands
		// flows straight back into the trigger beside the field.
		const stored = this.id
			? eventsState.value.find((e) => e.id === this.id)
			: undefined;
		// Each type's own resolved entry window and zone, joined onto the rows
		// the list already localizes — the calendar derives the day from the
		// window and from nothing else, so no stored day can contradict it.
		// `note` and `warning` arrive already localized because only this
		// component holds the catalog.
		const venues = Object.fromEntries(
			locationsState.value.map((l) => [l.id, l]),
		);
		// The id has to match before a single figure is read: this form also
		// opens standalone from the events grid, where the console's cell holds
		// another event's orders or none at all. Null covers the session that
		// may not read orders and the scope whose first snapshot has not landed
		// alike — a zero would report either of them as "none sold".
		const figures =
			consoleEventId.value === this.id ? consoleFiguresByType.get() : null;
		const calendarRows = displayedTicketTypeRows.flatMap((r) => {
			const type = stored
				? ticketTypesState.value.find((t) => t.id === r.id)
				: undefined;
			if (!stored || !type) return [];
			const entry = TicketType.resolveEntryWindow(type, stored);
			return [
				{
					id: r.id,
					name: r.name,
					price: r.price,
					capacity: r.capacity,
					// Null rather than 0 wherever the figure is unreadable: a surface
					// that cannot see sales states nothing, and 0 sold is a
					// fabrication the ring would then draw as an empty arc.
					sold: figures?.get(r.id)?.sold ?? null,
					revenue: figures?.get(r.id)?.revenue ?? null,
					entryWindow: {
						opensAt: entry.opensAt.toMillis(),
						closesAt: entry.closesAt.toMillis(),
					},
					timezone: TicketType.resolveEffectiveTimezone(type, stored, venues),
					// A type waiting on its predecessor is the operator's own
					// arrangement working, never a fault, so the chain sentence rides
					// the neutral meta line and error ink is spent only on the two
					// lines that ARE one. Price and capacity are absent from it: the
					// row prints the price under the name and the leading token's
					// centre carries the capacity, so a meta line stating either
					// would be the second place one fact lives.
					note: r.predecessorName
						? this.t("admin.events.form.ticketTypeShowsAfter", {
								name: r.predecessorName,
							})
						: null,
					warning: r.priceDescends
						? this.t("admin.events.form.priceDescendsWarning")
						: r.predecessorHasClosesAt
							? this.t("admin.events.form.predecessorClosesAtWarning")
							: null,
				},
			];
		});
		// What a keyboard reorder of the draft list reads out. The stored list
		// is <programme-calendar>'s, which carries its own copy of these — the
		// primitive's own defaults are English, and an operator working in
		// German is the one holding the keyboard.
		const reorderAnnouncements = {
			grabbed: (name: string, position: number, total: number) =>
				this.t("admin.events.form.reorderGrabbed", { name, position, total }),
			moved: (name: string, position: number, total: number) =>
				this.t("admin.events.form.reorderMoved", { name, position, total }),
			dropped: (name: string, position: number, total: number) =>
				this.t("admin.events.form.reorderDropped", { name, position, total }),
			cancelled: (name: string) =>
				this.t("admin.events.form.reorderCancelled", { name }),
		};
		// An ask sits at `requested` until the trigger picks it up, which is a
		// round trip the operator is watching the button through: both states
		// are one pass in progress as far as this surface is concerned.
		const descriptionPassRunning =
			stored?.descriptionByLocaleStatus?.state === "requested" ||
			stored?.descriptionByLocaleStatus?.state === "translating";

		return html`
			<div ${overflowWithin()} class="@container flex h-full flex-col min-w-[min(94vw,40rem)]">
			<!-- pb clears the sticky footer below: it overlays this scroll content,
			     so without the padding the last card's tail sits under it. -->
			<div ?inert=${this.saving} class="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-[minmax(0,42rem)_20rem] @5xl:items-start @5xl:justify-center @5xl:gap-8 p-4 pb-24 md:p-6 md:pb-28 lg:p-8 lg:pb-32">
				<div class="hidden w-full max-w-xs flex-col gap-3 @5xl:order-2 @5xl:flex @5xl:sticky @5xl:top-4">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.form.previewLabel")}</schmancy-typography>
					<event-tile .row=${this.previewRow()} .interactive=${false}></event-tile>
				</div>

				<div class="mx-auto flex w-full max-w-2xl flex-col gap-6 @5xl:order-1">
					<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
						<schmancy-typography type="title" token="md">${this.t("admin.events.form.sectionBasics")}</schmancy-typography>
					<!-- A new event starts here: the press that opened the form is
					     gone, and nothing else on the page owns the focus. -->
					<schmancy-input
						label=${this.t("admin.events.form.nameLabel")}
						required
						?autofocus=${!this.id}
						hint=${this.t("admin.events.form.nameHint")}
						.value=${this.name}
						@input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
					></schmancy-input>
					<schmancy-textarea
						label=${this.t("admin.events.form.descriptionLabel")}
						autoHeight
						hint=${this.t("admin.events.form.descriptionHint")}
						.value=${this.description}
						@change=${(e: CustomEvent<{ value: string }>) => (this.description = e.detail.value)}
					>
						<!-- Inside the field, in the row the textarea gives its actions
						     below the text: these are versions OF this description, and a
						     trigger parked under the field read as unrelated furniture,
						     worse the narrower the form got. Always mounted, even with
						     nothing stored behind it — the trigger disables itself, so
						     the control is where the operator will look for it before
						     there is anything to look at. Locked while the description is
						     unsaved for the same reason the bulk pass is: a pass is
						     written from the stored string, and this form's own Save
						     would retire the result the moment it lands a different
						     one. -->
						<localized-text
							slot="actions"
							multiline
							label=${this.t("admin.events.localize.descriptionLabel")}
							.source=${stored?.description ?? ""}
							.byLocale=${stored?.descriptionByLocale ?? null}
							?pending=${descriptionPassRunning}
							?disabled=${descriptionUnsaved}
							@save=${this.storeDescriptionCopy}
							@translate=${this.requestDescriptionPass}
						></localized-text>
					</schmancy-textarea>
					<localize-notice
						.source=${stored?.description ?? ""}
						?unsaved=${descriptionUnsaved}
						?failed=${stored?.descriptionByLocaleStatus?.state === "failed"}
					></localize-notice>
				</schmancy-surface>

				<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
					<schmancy-typography type="title" token="md">
						${this.t("admin.events.form.sectionScheduleVenue")}
					</schmancy-typography>
					<!-- Scheduling looks ahead: schmancy's default chips are reporting
					     ranges (Yesterday, Last 7 days) that land in the past. -->
					<schmancy-date-range
						type="datetime-local"
						hide-nav
						.presets=${[]}
						placeholder=${this.t("admin.events.form.schedulePlaceholder")}
						.start=${this.start || null}
						.end=${this.end || null}
						@change=${(
							e: CustomEvent<{ start: string | null; end: string | null }>,
						) => {
							this.start = e.detail.start ?? "";
							this.end = e.detail.end ?? "";
						}}
					></schmancy-date-range>
					<select-location
						label=${this.t("admin.events.form.venueLabel")}
						.value=${this.locationId}
						placeholder=${this.t("admin.events.form.venuePlaceholder")}
						@change=${(e: CustomEvent<{ value: string }>) => (this.locationId = e.detail.value)}
					></select-location>
				</schmancy-surface>

				<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
					<div class="flex items-center justify-between gap-2">
						<schmancy-typography type="title" token="md">
							${this.t("admin.events.form.sectionTicketTypes")}
						</schmancy-typography>
						<div class="flex items-center gap-1">
							<schmancy-icon-button
								variant="outlined"
								icon="add"
								aria-label=${this.t("admin.events.form.addTicketTypeAriaLabel")}
								@click=${() =>
									editing
										? this.openTicketTypeForm()
										: this.openTicketDraftForm()}
							></schmancy-icon-button>
						</div>
					</div>
					${when(
						editing,
						() => html`
							${when(
								ticketTypeRows.length === 0 && ticketTypesStatus !== "loading",
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${this.t("admin.events.form.noTicketTypesYet")}
									</schmancy-typography>
								`,
								() => html`
									<!-- Every programme day at once, the same band the console
									     shows: an operator working through a run reads one
									     surface, not two vocabularies for one catalog. An event
									     whose types name no day is the calendar's own headless
									     arm — one card of the same lines — so this form keeps no
									     second row shape of its own for that case. -->
									<programme-calendar
										editable
										.rows=${calendarRows}
										.timezone=${resolveVenueTimezone(
											this.locationId,
											locationsState.value,
										)}
										.event=${stored ?? null}
										@open=${(e: CustomEvent<{ id: string }>) =>
											this.openTicketTypeForm({ id: e.detail.id })}
										@duplicate=${(e: CustomEvent<{ id: string }>) =>
											this.openTicketTypeForm({ duplicateOf: e.detail.id })}
										@cloneDay=${(e: CustomEvent<{ day: string }>) =>
											this.openDayCloneSheet(e.detail.day)}
										@moveToDay=${(
											e: CustomEvent<{ id: string; day: string | null }>,
										) => {
											const type = ticketTypesState.value.find(
												(t) => t.id === e.detail.id,
											);
											if (!stored || !type) return;
											// A whole-event drop clears the window rather than
											// re-anchoring one: absence IS "admits on every day",
											// resolved against the event's own schedule at read time.
											let entryWindow: TicketTypeWrite["entryWindow"] = null;
											if (e.detail.day !== null) {
												// The type's own EFFECTIVE window, split on the same
												// predicate the calendar files its columns by, so the
												// surface and the write can never disagree about which
												// column a row came from. A day-bound window is an
												// offer of that many days and keeps its span. A
												// whole-event one has no span to keep: it is the
												// event's schedule inherited, so it names hours, not
												// days, and collapses onto the day it was dropped on.
												const current = TicketType.resolveEntryWindow(
													type,
													stored,
												);
												const wallClock = {
													opensAt: current.opensAt.toDate(),
													closesAt: current.closesAt.toDate(),
												};
												const timezone = TicketType.resolveEffectiveTimezone(
													type,
													stored,
													venues,
												);
												const reanchored = TicketType.coversWholeEvent(
													{
														opensAt: current.opensAt.toMillis(),
														closesAt: current.closesAt.toMillis(),
													},
													stored,
												)
													? TicketType.collapseWindowToDay(
															wallClock,
															e.detail.day,
															timezone,
														)
													: TicketType.reanchorWindowToDay(
															wallClock,
															e.detail.day,
															timezone,
														);
												// Null is the spring-forward gap, from either branch:
												// those hours do not exist on that day, so the move is
												// refused rather than silently shifted.
												if (reanchored === null) {
													$notify.error(
														this.t("admin.events.form.moveToDayFailed", {
															day: e.detail.day,
														}),
													);
													return;
												}
												entryWindow = reanchored;
											}
											// The `reorder` this move rides in on is a concurrent
											// write to the same doc's `order`, dispatched just
											// before it: the stored value is the pre-drag one, and
											// whichever write committed second would win. Both name
											// the position the drag just produced instead.
											const orderFromDrag =
												this.optimisticTicketTypeOrder?.indexOf(type.id) ?? -1;
											const body: TicketTypeWrite = {
												kind: type.kind,
												name: type.name,
												nameByLocale: type.nameByLocale,
												description: type.description ?? null,
												descriptionByLocale: type.descriptionByLocale,
												vatRate: type.vatRate,
												price: type.price,
												capacity: type.capacity,
												order:
													orderFromDrag === -1 ? type.order : orderFromDrag,
												showAfterSoldOut: type.showAfterSoldOut ?? null,
												claimableBy: type.claimableBy ?? null,
												verifyAtDoor: type.verifyAtDoor,
												doorNote: type.doorNote ?? null,
												volumeDiscounts: type.volumeDiscounts,
												salesWindow: type.salesWindow
													? {
															opensAt: type.salesWindow.opensAt.toDate(),
															closesAt: type.salesWindow.closesAt.toDate(),
														}
													: null,
												entryWindow,
												locationId: type.locationId ?? null,
												templateVariant: type.templateVariant ?? null,
											};
											ticketTypesAction$.next({
												kind: "update",
												requestId: crypto.randomUUID(),
												eventId: this.id,
												id: type.id,
												body,
											});
										}}
										@reorder=${(e: CustomEvent<{ orderedIds: string[] }>) => {
											this.optimisticTicketTypeOrder = e.detail.orderedIds;
											ticketTypesAction$.next({
												kind: "reorder",
												requestId: crypto.randomUUID(),
												eventId: this.id,
												orderedIds: e.detail.orderedIds,
											});
										}}
									></programme-calendar>
								`,
							)}
						`,
						() => html`
							${when(
								this.draftTicketTypes.length === 0,
								() => html`
									<div class="flex flex-col items-start gap-2">
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
											${this.t("admin.events.form.noTicketTypesHint")}
										</schmancy-typography>
										<schmancy-button variant="outlined" @click=${() => this.openTicketDraftForm()}>
											<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
											${this.t("admin.events.form.addFirstTicketType")}
										</schmancy-button>
									</div>
								`,
								() => html`
									<schmancy-list
										surface="transparent"
										${reorder(
											this.draftTicketTypes.map((d) => d.draftId),
											{ labels: reorderAnnouncements },
										)}
										@reorder=${(e: SchmancyReorderEvent) => {
											// A draft has no id on disk yet, so the drop reassigns
											// the array itself — the order IS the value the create
											// intent rides out on.
											this.draftTicketTypes = e.detail.orderedIds.flatMap(
												(id) =>
													this.draftTicketTypes.filter((d) => d.draftId === id),
											);
										}}
									>
										${repeat(
											this.draftTicketTypes,
											(d) => d.draftId,
											(d) => html`
												<schmancy-list-item
													rounded
													data-reorder-item=${d.draftId}
													aria-label=${d.name}
													@click=${() => this.openTicketDraftForm({ draftId: d.draftId })}
												>
													<!-- The handle is the span, not the glyph: <schmancy-icon> hides
													     ITSELF from assistive tech, so a handle drawn as one is named
													     and focusable and announced to nobody. -->
													<span
														slot="leading"
														data-reorder-handle
														class="grid shrink-0 place-items-center text-surface-onVariant"
														aria-label=${this.t(
															"admin.events.form.reorderTicketTypeAriaLabel",
															{ name: d.name },
														)}
														@click=${(e: Event) => e.stopPropagation()}
													>
														<schmancy-icon>drag_indicator</schmancy-icon>
													</span>
													<div class="min-w-0 flex-1">
														<schmancy-typography type="title" token="sm">${d.name}</schmancy-typography>
														<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
															${this.t(
																"admin.events.form.draftTicketTypeMeta",
																{
																	kind: this.t(
																		d.kind === "addon"
																			? "admin.ticketTypes.form.kindAddon"
																			: "admin.ticketTypes.form.kindTicket",
																	),
																	price: formatCurrency(
																		d.price / 100,
																		"EUR",
																		this.locale,
																	),
																	capacity: d.capacity,
																},
															)}
														</schmancy-typography>
													</div>
													<schmancy-icon-button
														slot="trailing"
														variant="text"
														icon="content_copy"
														aria-label=${this.t("admin.events.form.duplicateTicketTypeAriaLabel", { name: d.name })}
														@click=${(e: Event) => {
															e.stopPropagation();
															this.openTicketDraftForm({
																duplicateOf: d.draftId,
															});
														}}
													></schmancy-icon-button>
													<schmancy-icon-button
														variant="text"
														slot="trailing"
														icon="delete"
														aria-label=${this.t("admin.events.form.removeTicketTypeAriaLabel", { name: d.name })}
														@click=${(e: Event) => {
															e.stopPropagation();
															this.draftTicketTypes =
																this.draftTicketTypes.filter(
																	(dt) => dt.draftId !== d.draftId,
																);
														}}
													></schmancy-icon-button>
												</schmancy-list-item>
											`,
										)}
									</schmancy-list>
								`,
							)}
						`,
					)}
				</schmancy-surface>

				${when(
					editing,
					() => html`
						<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
							<schmancy-typography type="title" token="md">${this.t("admin.events.form.refund.section")}</schmancy-typography>

							<div class="flex flex-col gap-2">
								<div class="flex items-center gap-2">
									<schmancy-switch
										.checked=${this.refundOn}
										aria-label=${this.t("admin.events.form.refund.toggleOn")}
										@change=${(e: CustomEvent<{ value: boolean }>) => {
											if (e.detail.value === this.refundOn) return;
											this.refundOn = e.detail.value;
										}}
									></schmancy-switch>
									<schmancy-typography type="label" token="sm">
										${this.t(
											this.refundOn
												? "admin.events.form.refund.toggleOn"
												: "admin.events.form.refund.toggleOff",
										)}
									</schmancy-typography>
								</div>
								${when(
									!this.refundOn,
									() => html`
										<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
											${this.t("admin.events.form.refund.offHint")}
										</schmancy-typography>
									`,
								)}
							</div>

							${when(
								this.refundOn,
								() => html`
									<div class="flex flex-col gap-2">
										<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
											${this.t("admin.events.form.refund.windowLabel")}
										</schmancy-typography>
										<schmancy-date-range
											type="datetime-local"
											hide-nav
											.presets=${[]}
											placeholder=${this.t("admin.events.form.refund.windowPlaceholder")}
											.start=${this.refundStart || null}
											.end=${this.refundEnd || null}
											@change=${(
												e: CustomEvent<{
													start: string | null;
													end: string | null;
												}>,
											) => {
												this.refundStart = e.detail.start ?? "";
												this.refundEnd = e.detail.end ?? "";
											}}
										></schmancy-date-range>
									</div>

									<div class="flex flex-col gap-2">
										<div class="flex items-center gap-2">
											<schmancy-switch
												.checked=${this.paidBeforeOn}
												aria-label=${this.t("admin.events.form.refund.paidBeforeToggle")}
												@change=${(e: CustomEvent<{ value: boolean }>) => {
													if (e.detail.value === this.paidBeforeOn) return;
													this.paidBeforeOn = e.detail.value;
													// Never a fabricated "now": on shows an empty field
													// asking for a value, off clears what was there.
													this.paidBefore = "";
												}}
											></schmancy-switch>
											<schmancy-typography type="label" token="sm">
												${this.t("admin.events.form.refund.paidBeforeToggle")}
											</schmancy-typography>
										</div>
										${when(
											this.paidBeforeOn,
											() => html`
												<schmancy-input
													class="max-w-xs"
													type="datetime-local"
													label=${this.t("admin.events.form.refund.paidBeforeLabel")}
													.value=${this.paidBefore}
													@change=${(e: CustomEvent<{ value: string }>) => (this.paidBefore = e.detail.value)}
												></schmancy-input>
											`,
										)}
										<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
											${this.t(
												this.paidBeforeOn
													? "admin.events.form.refund.paidBeforeHint"
													: "admin.events.form.refund.paidBeforeOffHint",
											)}
										</schmancy-typography>
									</div>

									<div class="flex flex-col gap-2">
										<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
											${this.t("admin.events.form.refund.messageLabel")}
										</schmancy-typography>
										<schmancy-textarea
											autoHeight
											label=${this.t("admin.events.form.refund.messageHintDe")}
											placeholder=${this.t("admin.events.form.refund.messagePlaceholder")}
											.value=${this.refundMessageDe}
											@change=${(e: CustomEvent<{ value: string }>) => (this.refundMessageDe = e.detail.value)}
										></schmancy-textarea>
										<schmancy-textarea
											autoHeight
											label=${this.t("admin.events.form.refund.messageHintEn")}
											placeholder=${this.t("admin.events.form.refund.messagePlaceholder")}
											.value=${this.refundMessageEn}
											@change=${(e: CustomEvent<{ value: string }>) => (this.refundMessageEn = e.detail.value)}
										></schmancy-textarea>
									</div>

									<div class="flex flex-col gap-2">
										<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
											${this.t("admin.events.form.refund.typesLabel")}
										</schmancy-typography>
										<schmancy-chips
											class="w-full"
											multi
											wrap
											.value=${[...this.refundableTypeIds]}
											@change=${(e: CustomEvent<{ value: string[] }>) => (this.refundableTypeIds = e.detail.value)}
										>
											${repeat(
												ticketTypeRows,
												(r) => r.id,
												(r) =>
													html`<schmancy-filter-chip .value=${r.id}>${r.name}</schmancy-filter-chip>`,
											)}
										</schmancy-chips>

										<!-- One line in every state, so ticking the last excluded
										     chip swaps the sentence in place rather than collapsing
										     the card. -->
										${when(
											this.refundableTypeIds.length === 0,
											() => html`
												<schmancy-typography type="body" token="sm" role="alert" class="max-w-prose text-error-default">
													${this.t("admin.events.form.refund.typesNoneSelected")}
												</schmancy-typography>
											`,
											() =>
												refundExcluded.length > 0
													? html`
															<schmancy-typography type="body" token="sm" class="max-w-prose text-error-default">
																${this.t(
																	"admin.events.form.refund.typesNotRefundable",
																	{
																		names: refundExcluded
																			.map((r) => r.name)
																			.join(", "),
																	},
																)}
															</schmancy-typography>
														`
													: html`
															<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
																${this.t("admin.events.form.refund.typesAllSelected")}
															</schmancy-typography>
														`,
										)}

										<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
											${this.t("admin.events.form.refund.futureTypes")}
										</schmancy-typography>
									</div>
								`,
							)}
						</schmancy-surface>
					`,
				)}

				<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
					<schmancy-typography type="title" token="md">${this.t("admin.events.form.sectionTicketTemplate")}</schmancy-typography>
					<ticket-template-picker
						.value=${this.templateVariant}
						label=${this.t("admin.events.form.ticketTemplateLabel")}
						@change=${(e: CustomEvent<{ value: TicketTemplateVariant | null }>) => (this.templateVariant = e.detail.value)}
					></ticket-template-picker>

					<div class="flex flex-col gap-2">
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("admin.events.form.logoLabel")}
						</schmancy-typography>
						<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
							${this.t("admin.events.form.logoHint")}
						</schmancy-typography>
						${when(
							this.logoPreviewUrl,
							(url) => html`
								<img
									src=${url}
									alt=""
									class="h-16 w-auto max-w-full self-start rounded-lg bg-surface-containerHigh object-contain"
								/>
								<div class="flex flex-wrap gap-2">
									<schmancy-button variant="outlined" @click=${() => this.pickLogo()}>
										${this.t("admin.events.form.logoChangeButton")}
									</schmancy-button>
									<schmancy-button variant="outlined" color="error" @click=${() => this.removeLogo()}>
										${this.t("admin.events.form.logoRemoveButton")}
									</schmancy-button>
								</div>
							`,
							() => html`
								<schmancy-button variant="outlined" class="self-start" @click=${() => this.pickLogo()}>
									<schmancy-icon slot="prefix" aria-hidden="true">upload</schmancy-icon>
									${this.t("admin.events.form.logoChooseButton")}
								</schmancy-button>
							`,
						)}
						<input
							${ref(this.logoInputRef)}
							type="file"
							accept="image/jpeg,image/png,image/webp"
							class="hidden"
							@change=${(e: Event) => this.onLogoFileChange(e)}
						/>
					</div>
				</schmancy-surface>

				<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
					<schmancy-typography type="title" token="md">${this.t("admin.events.form.sectionArtwork")}</schmancy-typography>
					${choose(this.artworkChoice, [
						[
							"unset",
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t("admin.events.form.artworkOptionalHint")}
								</schmancy-typography>
								<div class="w-full max-w-xs">
									<schmancy-image-crop
										ratio=${Event.POSTER_RATIO}
										max-dimension=${Event.POSTER_HEIGHT}
										label="poster"
										@crop=${this.applyArtwork}
									></schmancy-image-crop>
								</div>
							`,
						],
						// Dormant — see the artworkChoice field comment above. No tile
						// sets "ai"; kept defined so re-enabling it is one tile, not a
						// re-plumb of generateArtwork()/selectAiCandidate().
						[
							"ai",
							() => html`
								<div class="flex items-center justify-between gap-2">
									<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
										${this.t("admin.events.form.generateWithAi")}
									</schmancy-typography>
									<schmancy-icon-button
										variant="text"
										icon="close"
										aria-label=${this.t("admin.events.form.chooseDifferentWayAriaLabel")}
										@click=${() => (this.artworkChoice = "unset")}
									></schmancy-icon-button>
								</div>
								<schmancy-input
									label=${this.t("admin.events.form.describePosterLabel")}
									placeholder=${this.t("admin.events.form.describePosterPlaceholder")}
									hint=${this.t("admin.events.form.describePosterHint")}
									.value=${this.aiPrompt}
									@change=${(e: CustomEvent<{ value: string }>) => (this.aiPrompt = e.detail.value)}
								></schmancy-input>
								<schmancy-button
									variant="outlined"
									class="self-start"
									?disabled=${this.aiGenerating}
									@click=${() => this.generateArtwork()}
								>
									${
										this.aiGenerating
											? html`<schmancy-spinner size="sm" slot="prefix"></schmancy-spinner>${this.t("admin.events.form.generating")}`
											: html`<schmancy-icon slot="prefix" aria-hidden="true">wand_stars</schmancy-icon>${this.t("admin.events.form.generate")}`
									}
								</schmancy-button>
								${when(
									!!this.aiError,
									() => html`
										<schmancy-typography type="body" token="sm" role="alert" class="text-error-default">
											${this.aiError}
										</schmancy-typography>
									`,
								)}
								<schmancy-result-grid
									.items=${this.aiCandidates.map((c) => ({ id: c.id, imageUrl: c.imageUrl }))}
									.loading=${this.aiGenerating}
									.selected=${this.aiSelectedId}
									ratio=${Event.POSTER_RATIO}
									label=${this.t("admin.events.form.posterConceptsLabel")}
									@select=${(e: CustomEvent<{ id: string }>) => this.selectAiCandidate(e.detail.id)}
									@regenerate=${() => this.generateArtwork()}
								></schmancy-result-grid>
							`,
						],
						[
							"done",
							() => html`
								<div class="flex w-full max-w-xs flex-col items-start gap-2">
									<div class="aspect-[2/3] w-full overflow-hidden rounded-2xl bg-surface-containerHigh">
										<img
											src=${this.posterPreview ?? ""}
											alt=""
											width=${Event.POSTER_WIDTH}
											height=${Event.POSTER_HEIGHT}
											decoding="async"
											class="h-full w-full object-cover"
										/>
									</div>
									<div class="flex flex-wrap gap-2">
										<schmancy-button variant="outlined" @click=${() => this.changeArtwork()}>${this.t("admin.events.form.changeArtwork")}</schmancy-button>
										<schmancy-button variant="outlined" color="error" @click=${() => this.removeArtwork()}>${this.t("admin.events.form.removeArtwork")}</schmancy-button>
									</div>
								</div>
							`,
						],
					])}
				</schmancy-surface>

				${when(
					editing,
					() => html`
						<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
							<schmancy-typography type="title" token="md">
								${this.t("admin.events.localize.section")}
							</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
								${this.t("admin.events.localize.sectionHint")}
							</schmancy-typography>
							${when(
								descriptionUnsaved,
								() => html`
									<schmancy-typography type="body" token="sm" class="max-w-prose text-error-default">
										${this.t("admin.events.localize.saveFirstHint")}
									</schmancy-typography>
								`,
							)}
							<schmancy-button
								variant="outlined"
								class="self-start"
								?disabled=${descriptionUnsaved}
								@click=${() =>
									show(() => html`<localize-copy></localize-copy>`, {
										ariaLabel: this.t("admin.events.localize.overlayAriaLabel"),
									})
										.pipe(takeUntil(this.disconnecting))
										.subscribe()}
							>
								<schmancy-icon slot="prefix" aria-hidden="true">translate</schmancy-icon>
								${this.t("admin.events.localize.openButton")}
							</schmancy-button>
						</schmancy-surface>
					`,
				)}

				<schmancy-surface type="containerLow" class="flex flex-col gap-4 rounded-2xl p-4 md:p-6">
					<schmancy-typography type="title" token="md">
						${this.t("admin.events.form.sectionStatusLimits")}
					</schmancy-typography>
					${when(
						editing,
						() => html`
							<div class="flex flex-col gap-2">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.form.statusLabel")}</schmancy-typography>
								<schmancy-chips
									class="w-full"
									required
									.value=${this.status}
									@change=${(e: CustomEvent<{ value: string }>) => (this.status = e.detail.value as Event.Event["status"])}
								>
									${repeat(
										STATUSES,
										(s) => s,
										(s) =>
											html`<schmancy-filter-chip .value=${s}>${this.t(`admin.common.status.${s}`)}</schmancy-filter-chip>`,
									)}
								</schmancy-chips>
							</div>
						`,
						() => html`
							<div class="flex flex-col gap-2">
								<div class="flex items-center gap-2">
									<schmancy-switch
										.checked=${this.status === "published"}
										aria-label=${this.t("admin.events.form.publishNow")}
										@change=${(e: CustomEvent<{ value: boolean }>) =>
											(this.status = e.detail.value ? "published" : "draft")}
									></schmancy-switch>
									<schmancy-typography type="label" token="sm">${this.t("admin.events.form.publishNow")}</schmancy-typography>
								</div>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t("admin.events.form.publishNowHint")}
								</schmancy-typography>
							</div>
						`,
					)}
					<schmancy-input
						label=${this.t("admin.events.form.maxTicketsLabel")}
						type="number"
						min="1"
						hint=${this.t("admin.events.form.maxTicketsHint")}
						.value=${this.maxTicketsPerOrder}
						@change=${(e: CustomEvent<{ value: string }>) => (this.maxTicketsPerOrder = e.detail.value)}
					></schmancy-input>
				</schmancy-surface>
				</div>
			</div>

			<schmancy-surface
				opacity="translucent"
				elevated
				class="sticky bottom-20 md:bottom-0 flex flex-wrap items-center gap-2 @5xl:gap-3 rounded-t-2xl px-4 pb-4 pt-2 md:px-6 md:pb-6 lg:px-8 lg:pb-8"
			>
				${when(
					editing,
					() => html`
						<schmancy-button variant="outlined" color="error" ?disabled=${this.saving && this.activeAction !== "delete"} .busy=${this.saving && this.activeAction === "delete"} @click=${() => this.deleteEvent()}>
							<span style="display:grid"><span style=${this.failedAction === "delete" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "delete" ? "true" : "false"}>${this.t("admin.common.delete")}</span><span style=${this.failedAction === "delete" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "delete" ? "false" : "true"}>${this.t("admin.common.retryDelete")}</span></span>
						</schmancy-button>
					`,
				)}
				<div class="ml-auto grid w-full grid-cols-3 items-stretch gap-2 md:flex md:w-auto md:justify-end">
					<schmancy-button class="w-full md:w-auto" variant="outlined" ?disabled=${this.saving} @click=${() => this.close()}>${this.t("admin.common.cancel")}</schmancy-button>
					<div class="min-w-0 @5xl:hidden">
						<schmancy-button
							class="w-full md:w-auto"
							variant="outlined"
							@click=${() => this.openPreview()}
						>
							${this.t("admin.events.form.previewLabel")}
						</schmancy-button>
					</div>
					<schmancy-button class="w-full md:w-auto" aria-label=${this.failedAction === "save" ? `${this.t("admin.common.retrySaveShort")}: ${this.t("admin.common.retrySave")}` : this.t("admin.common.save")} variant="outlined" ?disabled=${this.saving && this.activeAction !== "save"} .busy=${this.saving && this.activeAction === "save"} color=${this.failedAction === "save" ? "error" : "primary"} @click=${() => this.save()}>
						<span class="md:hidden">${this.t(this.failedAction === "save" ? "admin.common.retrySaveShort" : "admin.common.save")}</span>
						<span class="hidden md:grid"><span style=${this.failedAction === "save" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "save" ? "true" : "false"}>${this.t("admin.common.save")}</span><span style=${this.failedAction === "save" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "save" ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
					</schmancy-button>
				</div>
			</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-form": EventForm;
	}
}
